import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { BlockUIService } from '../../../services/block-ui.service';
import { TemplateCommonService } from '../../components/template-ai-common/services/template-common.service';
import { BrandIndexService } from '../../../services/brand-index.service';
import { CategoryService, CategoryAnalysisRequest } from '../../../services/category.service';
import { categorySampleData } from '../../sample-data/category';
import { CommonSourcesComponent } from '../../components/template-ai-common/common-sources/common-sources.component';

@Component({
  selector: 'app-category-analysis-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxChartsModule,
    CommonSourcesComponent,
    BarLineComboChartComponent,
    CompositeScoreWidgetComponent,
    // CitationBadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './category-analysis-tab.component.html',
  styleUrl: './category-analysis-tab.component.scss'
})
export class CategoryAnalysisTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() analysisConsumerPreference: any[] = [];
  @Input() data: any = null;
  @Input() brands: string[] = [];
  @Input() title: string = 'Category Analysis';

  // Legacy inputs for backward compatibility
  @Input() analysisCategory: any[] = [];

  // Core data properties
  categoryAnalysisResponse: any = null;
  compositeScore: number = 0;
  context: any = null;
  metadata: any = null;
  citations: any[] = [];

  // Chart data properties
  aiCategoryInsights: any = null;
  aiTableSummary: any = null;
  categoryDemand: any = null;
  subjectDemandAnalysis: any = null;
  universityInfo: any = null;
  recommendations: any = null;

  comboChartData: any[] = [];
  categoryDemandData: any[] = [];
  subjectDemandData: any[] = [];

  // Table data
  tableRows: any[] = [];

  // Info cards data
  infoCards: any[] = [];

  // UI state
  isLoading: boolean = false;
  error: string | null = null;

  // Chart configurations
  view: [number, number] = [400, 300];
  comboBarScheme: any = {
    domain: ['#2196f3', '#ff9800', '#4caf50', '#f44336', '#9c27b0']
  };
  lineChartScheme: any = {
    domain: ['#2196f3']
  };

  // Event emitters
  @Output() dataLoaded = new EventEmitter<any>();
  @Output() errorOccurred = new EventEmitter<string>();

  private destroy$ = new EventEmitter<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private blockUIService: BlockUIService,
    private templateCommonService: TemplateCommonService,
    private brandIndexService: BrandIndexService,
    private categoryService: CategoryService
  ) {}

  ngOnInit(): void {
    // Initialize with sample data if no input data is provided
    if (!this.data) {
      this.initializeWithSampleData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.processData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.emit();
    this.destroy$.complete();
  }

  private initializeWithSampleData(): void {
    // Load sample data by default
    this.processSampleData();
  }

  private processSampleData(): void {
    try {
      // Use the sample data directly as it now matches the new API response format
      const sampleResponse: any = categorySampleData;

      this.processResponseData(sampleResponse);
    } catch (error) {
      console.error('Error processing sample data:', error);
      this.error = 'Failed to process sample data';
    }
  }

  private processData(): void {
    if (!this.data) return;

    try {
      // Check if data has the expected structure (from transformed API response)
      if (this.data.composite_score || this.data.ai_category_insights || this.data.chart_data) {
        this.processResponseData(this.data);
      } else {
        // Convert legacy data format
        this.processLegacyData();
      }
    } catch (error) {
      console.error('Error processing category analysis data:', error);
      this.error = 'Failed to process category analysis data';
      this.errorOccurred.emit(this.error);
    }
  }

  private processResponseData(response: any): void {
    if (!response) {
      console.error('Category Analysis: No response data received');
      return;
    }

    this.categoryAnalysisResponse = response;

    // Handle both sample data structure and API response structure
    const compositeScoreData = response.composite_score || response.chart_data?.composite_score;
    this.compositeScore = compositeScoreData?.value || 0;

    this.context = response.context;
    this.metadata = response.model_meta;
    this.citations = response.citations || response.chart_data?.citations || [];

    // Extract data from the response structure (handle both sample and API formats)
    this.aiCategoryInsights = response.ai_category_insights || response.chart_data?.ai_category_insights;
    this.aiTableSummary = response.ai_table_summary || response.chart_data?.ai_table_summary;
    this.categoryDemand = response.category_demand || response.chart_data?.category_demand;
    this.subjectDemandAnalysis = response.subject_demand_analysis || response.chart_data?.subject_demand_analysis;
    this.universityInfo = response.university_info || response.chart_data?.university_info;
    this.recommendations = response.recommendations || response.chart_data?.recommendations;

    // Process chart data
    this.processChartData();

    // Process table data
    this.processTableData();

    // Process info cards
    this.processInfoCards();

    this.dataLoaded.emit(response);
    this.cdr.markForCheck();
  }

  private processLegacyData(): void {
    // Handle legacy data format if needed
  }

  private processChartData(): void {
    // Process AI Category Insights chart data
    if (this.aiCategoryInsights && this.aiCategoryInsights.series) {
      this.comboChartData = this.aiCategoryInsights.x_axis.map((category: string, index: number) => ({
        name: category,
        value: this.aiCategoryInsights.series[0].data[index]
      }));
    }

    // Process Category Demand chart data
    if (this.categoryDemand && this.categoryDemand.series) {
      this.categoryDemandData = this.categoryDemand.series.map((item: any) => ({
        name: item.name,
        value: item.value
      }));
    }

    // Process Subject Demand Analysis chart data
    if (this.subjectDemandAnalysis && this.subjectDemandAnalysis.series) {
      const seriesData = this.subjectDemandAnalysis.series[0];
      if (seriesData && seriesData.data) {
        // Filter out the last three columns: Domestic course fees, International course fees, Average salary
        const excludedKeys = ['Domestic course fees', 'International course fees', 'Average salary'];
        this.subjectDemandData = Object.entries(seriesData.data)
          .filter(([key, value]) => !excludedKeys.includes(key))
          .map(([key, value]) => ({
            name: key,
            value: typeof value === 'string' ? parseFloat(value.toString().replace(/[£,]/g, '')) || 0 : value
          }));
      }
    }
  }

  private processTableData(): void {
    if (this.aiTableSummary) {
      this.tableRows = this.aiTableSummary.rows.map((row: any[], index: number) => ({
        id: index,
        category: row[0],
        score: row[1]
      }));
    }
  }

  private processInfoCards(): void {
    if (this.universityInfo) {
      this.infoCards = Object.entries(this.universityInfo).map(([key, value]) => ({
        title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value
      }));
    }
  }

  refreshData(): void {
    this.isLoading = true;
    this.error = null;
    this.cdr.markForCheck();

    // Create API request
    const request: CategoryAnalysisRequest = {
      brands: this.brands.length > 0 ? this.brands : ['Hull University'],
      category: 'Technology',
      region: 'North America',
      include_competitors: true,
      force_refresh: false
    };

    // Make API call
    this.categoryService.analyzeCategory(request).subscribe({
      next: (response) => {
        // console.log('Category Analysis: API response received:', response);
        this.isLoading = false;
        this.processResponseData(response);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        this.error = 'Failed to load category analysis data';
        console.error('Category analysis error:', error);
        this.cdr.markForCheck();
      }
    });
  }

  onSelect(event: any): void {
    // console.log('Chart item selected:', event);
  }

  onActivate(event: any): void {
    // console.log('Chart item activated:', event);
  }

  onDeactivate(event: any): void {
    // console.log('Chart item deactivated:', event);
  }

  getChartSubtitle(): string {
    return this.context?.analysis_type || 'Category Analysis';
  }

  hasData(): boolean {
    return !!(this.categoryAnalysisResponse && this.categoryAnalysisResponse.universities?.length > 0);
  }

  getOverallScore(): number {
    return this.compositeScore || 0;
  }

  getUniversitiesCount(): number {
    return this.categoryAnalysisResponse?.universities?.length || 0;
  }

  getDataStatus(): string {
    if (!this.categoryAnalysisResponse) {
      return 'No data available';
    }
    return 'Data loaded successfully';
  }

  shouldShowComboChart(): boolean {
    return this.comboChartData && this.comboChartData.length > 0;
  }

  shouldShowBarChart(): boolean {
    return this.comboChartData && this.comboChartData.length === 1;
  }

  get firstUniversityName(): string {
    return this.comboChartData && this.comboChartData.length > 0 ? this.comboChartData[0].name : 'University';
  }

  getUniversityName(): string {
    return this.categoryAnalysisResponse?.universities && this.categoryAnalysisResponse.universities.length > 0 ? this.categoryAnalysisResponse.universities[0].name : 'University';
  }
}

